import React, { useState, useEffect } from 'react'
import { StatsCard } from './StatsCard'
import {
  BanknotesIcon,
  CreditCardIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline'
import { dashboardAPI, type DashboardMetrics } from '@/services/api'
import { useAuthStore } from '@/stores/authStore'
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts'

export const Dashboard: React.FC = () => {
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { user } = useAuthStore()

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        setLoading(true)
        const data = await dashboardAPI.getMetrics()
        setMetrics(data)
      } catch (err: any) {
        setError(err.message || 'Erro ao carregar métricas')
      } finally {
        setLoading(false)
      }
    }

    fetchMetrics()
    // Refresh every 30 seconds
    const interval = setInterval(fetchMetrics, 30000)
    return () => clearInterval(interval)
  }, [])

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value)
  }

  // Mock data for charts (will be replaced with real API data)
  const chartColors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4']

  const monthlyData = [
    { month: 'Jan', receitas: 15000, despesas: 12000, saldo: 3000 },
    { month: 'Fev', receitas: 18000, despesas: 13500, saldo: 4500 },
    { month: 'Mar', receitas: 16500, despesas: 14000, saldo: 2500 },
    { month: 'Abr', receitas: 20000, despesas: 15000, saldo: 5000 },
    { month: 'Mai', receitas: 22000, despesas: 16000, saldo: 6000 },
    { month: 'Jun', receitas: 25000, despesas: 18000, saldo: 7000 }
  ]

  const expenseCategories = metrics?.expense_categories || [
    { category: 'Alimentação', amount: 2500, percentage: 35 },
    { category: 'Transporte', amount: 1800, percentage: 25 },
    { category: 'Moradia', amount: 1200, percentage: 17 },
    { category: 'Lazer', amount: 900, percentage: 13 },
    { category: 'Outros', amount: 700, percentage: 10 }
  ]

  const weeklyActivity = [
    { day: 'Seg', transacoes: 12 },
    { day: 'Ter', transacoes: 8 },
    { day: 'Qua', transacoes: 15 },
    { day: 'Qui', transacoes: 6 },
    { day: 'Sex', transacoes: 18 },
    { day: 'Sab', transacoes: 4 },
    { day: 'Dom', transacoes: 2 }
  ]

  const customTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-medium text-gray-900">{`${label}`}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} style={{ color: entry.color }} className="text-sm">
              {`${entry.dataKey}: ${entry.dataKey.includes('saldo') || entry.dataKey.includes('receitas') || entry.dataKey.includes('despesas')
                ? formatCurrency(entry.value)
                : entry.value}`}
            </p>
          ))}
        </div>
      )
    }
    return null
  }

  const statsData = metrics ? [
    {
      title: 'Saldo Total',
      value: formatCurrency(metrics.total_balance || 0),
      change: metrics.monthly_growth || 0,
      changeType: (metrics.monthly_growth || 0) >= 0 ? 'increase' as const : 'decrease' as const,
      icon: <BanknotesIcon className="w-6 h-6" />,
      color: 'green' as const
    },
    {
      title: 'Receitas',
      value: formatCurrency(metrics.monthly_income || 0),
      change: 8.2,
      changeType: 'increase' as const,
      icon: <ArrowTrendingUpIcon className="w-6 h-6" />,
      color: 'blue' as const
    },
    {
      title: 'Despesas',
      value: formatCurrency(metrics.monthly_expenses || 0),
      change: 3.1,
      changeType: 'decrease' as const,
      icon: <ArrowTrendingDownIcon className="w-6 h-6" />,
      color: 'red' as const
    },
    {
      title: 'Investimentos',
      value: formatCurrency(metrics.investments_value || 0),
      change: 15.8,
      changeType: 'increase' as const,
      icon: <ChartBarIcon className="w-6 h-6" />,
      color: 'purple' as const
    }
  ] : []

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-6 text-white">
          <div className="animate-pulse">
            <div className="h-8 bg-blue-400 rounded mb-2"></div>
            <div className="h-4 bg-blue-300 rounded w-3/4"></div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <div className="animate-pulse">
                <div className="h-4 bg-gray-200 rounded mb-4"></div>
                <div className="h-8 bg-gray-200 rounded"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6">
          <h3 className="text-red-800 font-semibold mb-2">Erro ao carregar dados</h3>
          <p className="text-red-600">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-6 text-white">
        <h2 className="text-2xl font-bold mb-2">Olá, {user?.name || 'Usuário'}! 👋</h2>
        <p className="text-blue-100">
          Bem-vindo ao seu painel financeiro. Aqui está um resumo das suas finanças hoje.
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statsData.map((stat, index) => (
          <StatsCard key={index} {...stat} />
        ))}
      </div>

      {/* Monthly Financial Flow */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Fluxo Financeiro Mensal
        </h3>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={monthlyData}>
              <defs>
                <linearGradient id="colorReceitas" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10B981" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#10B981" stopOpacity={0.1}/>
                </linearGradient>
                <linearGradient id="colorDespesas" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#EF4444" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#EF4444" stopOpacity={0.1}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" stroke="#6b7280" />
              <YAxis stroke="#6b7280" tickFormatter={(value) => `R$ ${(value/1000).toFixed(0)}k`} />
              <Tooltip content={customTooltip} />
              <Legend />
              <Area
                type="monotone"
                dataKey="receitas"
                stackId="1"
                stroke="#10B981"
                fill="url(#colorReceitas)"
                name="Receitas"
              />
              <Area
                type="monotone"
                dataKey="despesas"
                stackId="2"
                stroke="#EF4444"
                fill="url(#colorDespesas)"
                name="Despesas"
              />
              <Line
                type="monotone"
                dataKey="saldo"
                stroke="#3B82F6"
                strokeWidth={3}
                name="Saldo"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Expense Categories - Pie Chart */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Despesas por Categoria
          </h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={expenseCategories}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={120}
                  dataKey="amount"
                  nameKey="category"
                  label={({ category, percentage }) => `${category} (${percentage}%)`}
                >
                  {expenseCategories.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={chartColors[index % chartColors.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => formatCurrency(Number(value))} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Weekly Activity */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Atividade Semanal
          </h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={weeklyActivity}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="day" stroke="#6b7280" />
                <YAxis stroke="#6b7280" />
                <Tooltip
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      return (
                        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
                          <p className="font-medium text-gray-900">{`${label}`}</p>
                          <p className="text-sm text-blue-600">
                            {`Transações: ${payload[0].value}`}
                          </p>
                        </div>
                      )
                    }
                    return null
                  }}
                />
                <Bar
                  dataKey="transacoes"
                  fill="#3B82F6"
                  radius={[4, 4, 0, 0]}
                  name="Transações"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Recent Transactions */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Transações Recentes
        </h3>
        {metrics?.recent_transactions && metrics.recent_transactions.length > 0 ? (
          <div className="space-y-4">
            {metrics.recent_transactions.map((transaction, index) => (
              <div key={transaction.id || index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">{transaction.description}</p>
                  <p className="text-sm text-gray-500">
                    {new Date(transaction.date).toLocaleDateString('pt-BR')}
                    {transaction.category && ` • ${transaction.category}`}
                  </p>
                </div>
                <span className={`font-semibold ${
                  transaction.type === 'INCOME' ? 'text-green-600' :
                  transaction.type === 'EXPENSE' ? 'text-red-600' : 'text-blue-600'
                }`}>
                  {transaction.type === 'EXPENSE' ? '-' : '+'}
                  {formatCurrency(Math.abs(transaction.amount))}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500">Nenhuma transação encontrada</p>
            <p className="text-sm text-gray-400 mt-1">
              Adicione suas primeiras transações para começar
            </p>
          </div>
        )}
      </div>
    </div>
  )
}